import { Cause, Effect, Exit, Fiber, Layer, Ref, Scope } from "effect";
import { Atom, AtomRegistry } from "effect/unstable/reactivity";
import { INTERNAL_HTTP_MESSAGE } from "@workerstation/shared/errors/http";
import { WorkspaceChangesApi } from "../../api/workspace-changes/service.ts";
import { historyFailureMessage } from "../../api/history/failure.ts";
import { makeSessionOwner } from "../../state/session-owner.ts";
import { QueryWork } from "../../state/query-work.ts";
import { baselineSelectionKey, baselineSnapshot, emptyBaselineState, type BaselineRequest, type BaselineSelection, type BaselineState } from "./baseline-model.ts";
import { WorkspaceChangesSessions, type WorkspaceChangesSession } from "./service.ts";
import { makeBaselineQueryOwner } from "./query-owner.ts";
const closed = (scope: Scope.Scope): boolean => scope.state._tag === "Closed";
export const WorkspaceChangesSessionsLive = (root: Scope.Scope) => Layer.effect(WorkspaceChangesSessions, Effect.gen(function* () {
  const api = yield* WorkspaceChangesApi, queries = yield* QueryWork, registry = yield* AtomRegistry.AtomRegistry;
  const sessions = yield* makeSessionOwner(root);
  const make = Effect.gen(function* () {
    const owner = yield* Scope.Scope, state = yield* Ref.make(emptyBaselineState());
    const failures = yield* Ref.make<readonly Cause.Reason<never>[]>([]);
    const read = Ref.get(state).pipe(Effect.map(baselineSnapshot));
    const snapshot = Atom.make(read);
    const retain = <A>(exit: Exit.Exit<A>) => Exit.isFailure(exit) ? Ref.update(failures, values => [...values, ...exit.cause.reasons.filter(reason => reason._tag === "Die")]) : Effect.void;
    const publish = Effect.gen(function* () {
      if (closed(root) || closed(owner)) return;
      const result = yield* Effect.exit(Effect.sync(() => registry.refresh(snapshot))); yield* retain(result); return yield* result;
    });
    yield* Effect.addFinalizer(() => Ref.get(failures).pipe(Effect.flatMap(reasons => reasons.length ? Effect.failCause(Cause.fromReasons(reasons)) : Effect.void)));
    const queryOwner = yield* makeBaselineQueryOwner(owner, retain);
    yield* Effect.addFinalizer(() => Ref.getAndUpdate(state, current => ({ ...current, closed: true, request: null })).pipe(
      Effect.flatMap(previous => previous.request ? previous.request.close : Effect.void)));
    const active = (current: BaselineState, request: BaselineRequest) => !current.closed && !closed(root) && !closed(owner)
      && !closed(request.scope) && current.request?.identity === request.identity
      && current.selection !== null && baselineSelectionKey(current.selection) === request.selectionKey;
    const finish = (request: BaselineRequest, change: (current: BaselineState) => BaselineState) => Ref.modify(state, current => active(current, request)
      ? [true, { ...change(current), request: null }] as const : [false, current] as const).pipe(Effect.flatMap(changed => changed ? publish : Effect.void));
    const select = Effect.fn("Frontend.WorkspaceChanges.select")(function* (selection: BaselineSelection) {
      const before = yield* Ref.get(state), key = baselineSelectionKey(selection);
      if (before.closed || closed(root) || closed(owner) || (before.selection !== null && baselineSelectionKey(before.selection) === key)) return;
      const childOwner = selection.workspaceId === null ? null : yield* queryOwner.open;
      const request: BaselineRequest | null = childOwner === null ? null : { ...childOwner, selectionKey: key };
      yield* Ref.set(state, { ...before, selection, error: null, request,
        ...(selection.workspaceId === null ? { data: null } : {}) });
      yield* publish;
      if (before.request) yield* before.request.close;
      if (request === null || selection.workspaceId === null) return;
      const workspaceId = selection.workspaceId;
      const operation = Effect.suspend(() => api.baseline(workspaceId)).pipe(
        Effect.matchEffect({
          onFailure: failure => finish(request, current => ({ ...current, data: null, error: { workspaceId, message: historyFailureMessage(failure) } })),
          onSuccess: baseline => finish(request, current => baseline.workspaceId !== workspaceId
            ? { ...current, data: null, error: { workspaceId, message: INTERNAL_HTTP_MESSAGE } }
            : { ...current, data: baseline, error: null })
        }),
        Effect.catchCause(cause => Effect.gen(function* () {
          yield* Ref.update(failures, values => [...values, ...cause.reasons.filter(reason => reason._tag === "Die")]);
          if (cause.reasons.every(reason => reason._tag === "Interrupt")) return;
          yield* finish(request, current => ({ ...current, data: null, error: { workspaceId, message: INTERNAL_HTTP_MESSAGE } }));
        })),
        Effect.onExit(retain)
      );
      const child = yield* Effect.forkIn(queries.observe(operation), request.scope, { uninterruptible: false });
      yield* Fiber.join(child).pipe(Effect.ensuring(request.close));
    });
    return { read, snapshot, select } satisfies WorkspaceChangesSession;
  });
  return WorkspaceChangesSessions.of({ open: sessions.open(make) });
}));
